/**
 * LLMConfigTab Component
 * Modern, elegant LLM configuration interface
 */

import { useState, useEffect } from 'react';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import {
  IconBrain,
  IconSparkles,
  IconBrandGoogle,
  IconCheck,
  IconInfoCircle,
  IconEye,
  IconEyeOff,
  IconCpu,
  IconShieldLock,
  IconTrash,
  IconKey,
  IconAlertTriangle,
} from '@tabler/icons-react';
import { Modal } from 'react-bootstrap';
import { useAgentsFlow } from '../../contexts/AgentsFlowContext';
import { LLM_PROVIDERS, LLM_SETTINGS } from '../../constants';
import AgentsFlowService from '../../services/AgentsFlowService';
import './LLMConfigTab.css';

// ===============================================
// EMBEDDING MODELS CATALOG (espelha ai_lang/llm/provider.py)
// ===============================================

const EMBEDDING_MODELS_BY_PROVIDER = {
  openai: [
    { id: 'text-embedding-3-small', name: 'OpenAI text-embedding-3-small (1536d)' },
    // { id: 'text-embedding-3-large', name: 'OpenAI text-embedding-3-large (3072d)' }, MANTER FORA DE USO POR ENQUANTO.
  ],
  google: [
    { id: 'text-embedding-004', name: 'Google text-embedding-004 (768d)' },
  ],
};

const AUX_PROVIDERS = ['openai', 'google'];

// ===============================================
// PROVIDER ICONS & COLORS
// ===============================================

const PROVIDER_CONFIG = {
  openai: {
    icon: IconBrain,
    color: '#10a37f',
    gradient: 'linear-gradient(135deg, #10a37f 0%, #0d8a6a 100%)',
    lightBg: '#ecfdf5',
  },
  anthropic: {
    icon: IconSparkles,
    color: '#d97706',
    gradient: 'linear-gradient(135deg, #d97706 0%, #b45309 100%)',
    lightBg: '#fffbeb',
  },
  google: {
    icon: IconBrandGoogle,
    color: '#4285f4',
    gradient: 'linear-gradient(135deg, #4285f4 0%, #1a73e8 100%)',
    lightBg: '#eff6ff',
  },
};

// ===============================================
// PROVIDER SELECTOR (Compact Pills)
// ===============================================

const ProviderSelector = ({ providers, selected, onSelect }) => {
  return (
    <div className="llm-providers">
      {Object.values(providers).map((provider) => {
        const config = PROVIDER_CONFIG[provider.id] || PROVIDER_CONFIG.openai;
        const Icon = config.icon;
        const isSelected = selected === provider.id;

        return (
          <button
            key={provider.id}
            className={`llm-provider ${isSelected ? 'llm-provider--active' : ''}`}
            onClick={() => onSelect(provider.id)}
            style={isSelected ? { '--provider-color': config.color, '--provider-bg': config.lightBg } : {}}
          >
            <div
              className="llm-provider__icon"
              style={isSelected ? { background: config.gradient } : {}}
            >
              <Icon size={18} />
            </div>
            <div className="llm-provider__info">
              <span className="llm-provider__name">{provider.name}</span>
              <span className="llm-provider__models">{provider.models.length} modelos</span>
            </div>
            {isSelected && (
              <div className="llm-provider__check">
                <IconCheck size={14} />
              </div>
            )}
          </button>
        );
      })}
    </div>
  );
};

// ===============================================
// MODEL SELECTOR (Modern Cards)
// ===============================================

const ModelSelector = ({ provider, selectedModel, onSelect }) => {
  if (!provider) return null;
  const providerData = LLM_PROVIDERS[provider];
  if (!providerData) return null;
  const config = PROVIDER_CONFIG[provider] || PROVIDER_CONFIG.openai;

  return (
    <div className="llm-models">
      {providerData.models.map((model) => {
        const isSelected = selectedModel === model.id;

        return (
          <div
            key={model.id}
            className={`llm-model ${isSelected ? 'llm-model--active' : ''}`}
            onClick={() => onSelect(model.id)}
            style={isSelected ? { '--model-color': config.color } : {}}
          >
            <div className="llm-model__radio">
              {isSelected && <div className="llm-model__radio-dot" style={{ background: config.color }} />}
            </div>
            <div className="llm-model__content">
              <div className="llm-model__header">
                <span className="llm-model__name">{model.name}</span>
                {model.recommended && (
                  <span className="llm-model__badge">Recomendado</span>
                )}
              </div>
              <span className="llm-model__desc">{model.description}</span>
            </div>
          </div>
        );
      })}
    </div>
  );
};

// ===============================================
// MAIN COMPONENT
// ===============================================

const LLMConfigTab = () => {
  const { state, updateLLMConfig } = useAgentsFlow();
  const { editor, vaultKeys } = state;

  const [config, setConfig] = useState({
    provider: 'openai',
    model: 'gpt-4.1-mini',
    max_tokens: 4096,
    fallback_enabled: true,
    custom_api_key: '',
    use_custom_key: false,
    vault_key_id: null,
  });
  const [showApiKey, setShowApiKey] = useState(false);
  const [newApiKey, setNewApiKey] = useState('');
  const [keyMode, setKeyMode] = useState('vault'); // 'vault' | 'direct'
  const [showMigrateModal, setShowMigrateModal] = useState(false);
  const [migrating, setMigrating] = useState(false);
  const [migrateError, setMigrateError] = useState('');

  const hasCustomKey = editor?.llm_config?.has_custom_key || false;

  // Detecta se o usuário alterou embedding_provider/embedding_model em relação ao salvo
  const savedEmbProvider = editor?.llm_config?.embedding_provider || '';
  const savedEmbModel = editor?.llm_config?.embedding_model || '';
  const embeddingChanged = (
    config.provider === 'anthropic'
    && savedEmbProvider
    && (
      (config.embedding_provider || '') !== savedEmbProvider
      || (config.embedding_model || '') !== savedEmbModel
    )
  );

  useEffect(() => {
    if (editor?.llm_config) {
      setConfig(prev => ({ ...prev, ...editor.llm_config }));
      setNewApiKey('');
      // Determine key mode from saved config
      if (editor.llm_config.vault_key_id) {
        setKeyMode('vault');
      } else if (editor.llm_config.use_custom_key) {
        setKeyMode('direct');
      }
    }
  }, [editor?.llm_config]);

  const filteredVaultKeys = vaultKeys.filter(k => k.provider === config.provider);

  const handleChange = (key, value) => {
    const newConfig = { ...config, [key]: value };
    setConfig(newConfig);
    updateLLMConfig(newConfig);
  };

  const handleApiKeyChange = (value) => {
    setNewApiKey(value);
    if (value) {
      const newConfig = { ...config, custom_api_key: value, use_custom_key: true, vault_key_id: null };
      setConfig(newConfig);
      updateLLMConfig(newConfig);
    } else if (hasCustomKey) {
      const newConfig = { ...config, custom_api_key: '', use_custom_key: false, vault_key_id: null };
      setConfig(newConfig);
      updateLLMConfig(newConfig);
    }
  };

  const handleVaultKeySelect = (vaultKeyId) => {
    if (!vaultKeyId) {
      // Cleared selection
      const newConfig = { ...config, vault_key_id: null };
      setConfig(newConfig);
      updateLLMConfig(newConfig);
      return;
    }
    const newConfig = { ...config, vault_key_id: vaultKeyId, custom_api_key: '', use_custom_key: false };
    setConfig(newConfig);
    updateLLMConfig(newConfig);
  };

  const handleProviderChange = (provider) => {
    const defaultModel = LLM_PROVIDERS[provider]?.models[0]?.id || '';
    const newConfig = { ...config, provider, model: defaultModel, vault_key_id: null };
    setConfig(newConfig);
    updateLLMConfig(newConfig);
  };

  return (
    <div className="llm-config">
      {/* Provider Selection */}
      <section className="llm-section">
        <div className="llm-section__header">
          <h3 className="llm-section__title">Provedor de IA</h3>
        </div>
        <ProviderSelector
          providers={LLM_PROVIDERS}
          selected={config.provider}
          onSelect={handleProviderChange}
        />

        {config.provider === 'anthropic' && (
          <>
            <div className="llm-provider-warning">
              <IconAlertTriangle size={16} className="llm-provider-warning__icon" />
              <div className="llm-provider-warning__text">
                <strong>Claude não tem áudio nem embeddings nativos.</strong>{' '}
                Selecione abaixo quais providers usar para transcrição de áudio
                (WhatsApp/Instagram/Messenger) e base de conhecimento (RAG).
              </div>
            </div>

            {/* Provider auxiliar para áudio */}
            <div className="llm-aux-config">
              <label className="llm-aux-config__label">
                Provider para transcrição de áudio
              </label>
              <select
                className="llm-apikey__select"
                value={config.audio_provider || ''}
                onChange={(e) => handleChange('audio_provider', e.target.value || null)}
              >
                <option value="">Selecione um provider...</option>
                {AUX_PROVIDERS.map(p => (
                  <option key={p} value={p}>
                    {LLM_PROVIDERS[p]?.name || p}
                  </option>
                ))}
              </select>

              {config.audio_provider && (
                <>
                  <label className="llm-aux-config__label">
                    Chave do cofre para áudio
                  </label>
                  <select
                    className="llm-apikey__select"
                    value={config.audio_vault_key_id || ''}
                    onChange={(e) => handleChange('audio_vault_key_id', e.target.value || null)}
                  >
                    <option value="">Selecione uma chave...</option>
                    {vaultKeys
                      .filter(k => k.provider === config.audio_provider)
                      .map(k => (
                        <option key={k.id} value={k.id}>
                          {k.label} ({k.key})
                        </option>
                      ))}
                  </select>
                  {vaultKeys.filter(k => k.provider === config.audio_provider).length === 0 && (
                    <span className="llm-apikey__vault-empty">
                      Nenhuma chave de {LLM_PROVIDERS[config.audio_provider]?.name || config.audio_provider} no cofre. Cadastre na aba "API Keys".
                    </span>
                  )}
                </>
              )}
            </div>

            {/* Provider auxiliar para embeddings */}
            <div className="llm-aux-config">
              <label className="llm-aux-config__label">
                Provider para base de conhecimento (embeddings)
              </label>
              <select
                className="llm-apikey__select"
                value={config.embedding_provider || ''}
                onChange={(e) => {
                  const v = e.target.value || null;
                  const defaultModel = v && EMBEDDING_MODELS_BY_PROVIDER[v]?.[0]?.id;
                  const newConfig = {
                    ...config,
                    embedding_provider: v,
                    embedding_model: defaultModel || null,
                    embedding_vault_key_id: null,
                  };
                  setConfig(newConfig);
                  updateLLMConfig(newConfig);
                }}
              >
                <option value="">Selecione um provider...</option>
                {AUX_PROVIDERS.map(p => (
                  <option key={p} value={p}>
                    {LLM_PROVIDERS[p]?.name || p}
                  </option>
                ))}
              </select>

              {config.embedding_provider && (
                <>
                  <label className="llm-aux-config__label">
                    Modelo de embeddings
                  </label>
                  <select
                    className="llm-apikey__select"
                    value={config.embedding_model || ''}
                    onChange={(e) => handleChange('embedding_model', e.target.value || null)}
                  >
                    <option value="">Selecione um modelo...</option>
                    {(EMBEDDING_MODELS_BY_PROVIDER[config.embedding_provider] || []).map(m => (
                      <option key={m.id} value={m.id}>{m.name}</option>
                    ))}
                  </select>

                  <label className="llm-aux-config__label">
                    Chave do cofre para embeddings
                  </label>
                  <select
                    className="llm-apikey__select"
                    value={config.embedding_vault_key_id || ''}
                    onChange={(e) => handleChange('embedding_vault_key_id', e.target.value || null)}
                  >
                    <option value="">Selecione uma chave...</option>
                    {vaultKeys
                      .filter(k => k.provider === config.embedding_provider)
                      .map(k => (
                        <option key={k.id} value={k.id}>
                          {k.label} ({k.key})
                        </option>
                      ))}
                  </select>
                  {vaultKeys.filter(k => k.provider === config.embedding_provider).length === 0 && (
                    <span className="llm-apikey__vault-empty">
                      Nenhuma chave de {LLM_PROVIDERS[config.embedding_provider]?.name || config.embedding_provider} no cofre. Cadastre na aba "API Keys".
                    </span>
                  )}
                </>
              )}

              {embeddingChanged && (
                <div className="llm-aux-config__migrate-warning">
                  <IconAlertTriangle size={14} />
                  <span>
                    Você alterou o modelo de embeddings. Documentos já indexados
                    com <strong>{savedEmbModel || 'o modelo anterior'}</strong> precisam
                    ser apagados antes da troca — o frontend NÃO salva a mudança
                    automaticamente.
                  </span>
                  <button
                    type="button"
                    className="llm-aux-config__migrate-btn"
                    onClick={() => setShowMigrateModal(true)}
                  >
                    Apagar base e trocar modelo
                  </button>
                </div>
              )}
            </div>
          </>
        )}
      </section>

      {/* Modal de confirmação destrutiva */}
      <Modal show={showMigrateModal} onHide={() => !migrating && setShowMigrateModal(false)} centered>
        <Modal.Header closeButton={!migrating}>
          <Modal.Title>Apagar base e trocar modelo de embeddings</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            Esta operação é <strong>destrutiva e irreversível</strong>. Ela irá:
          </p>
          <ul>
            <li>Apagar todos os documentos da base de conhecimento deste agente</li>
            <li>Apagar todos os chunks/vetores indexados no Redis</li>
            <li>
              Trocar o modelo de embeddings para
              {' '}<strong>{config.embedding_provider}/{config.embedding_model}</strong>
            </li>
          </ul>
          <p>
            Depois você precisa fazer <strong>re-upload manual</strong> dos documentos
            para que sejam indexados com o novo modelo.
          </p>
          {migrateError && (
            <div className="llm-aux-config__migrate-error">{migrateError}</div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <button
            type="button"
            className="btn btn-light"
            disabled={migrating}
            onClick={() => setShowMigrateModal(false)}
          >
            Cancelar
          </button>
          <button
            type="button"
            className="btn btn-danger"
            disabled={migrating || !config.embedding_provider || !config.embedding_model}
            onClick={async () => {
              setMigrating(true);
              setMigrateError('');
              try {
                const response = await AgentsFlowService.migrateEmbeddings(editor.id, {
                  embedding_provider: config.embedding_provider,
                  embedding_model: config.embedding_model,
                  embedding_vault_key_id: config.embedding_vault_key_id || null,
                  confirm: true,
                });
                const updated = response.data?.llm_config || {};
                updateLLMConfig({ ...config, ...updated });
                setShowMigrateModal(false);
              } catch (err) {
                setMigrateError(
                  err?.response?.data?.detail
                  || err?.message
                  || 'Erro ao migrar embeddings'
                );
              } finally {
                setMigrating(false);
              }
            }}
          >
            {migrating ? 'Apagando...' : 'Confirmar e apagar'}
          </button>
        </Modal.Footer>
      </Modal>

      {/* API Key */}
      <section className="llm-section">
        <div className="llm-section__header">
          <h3 className="llm-section__title">API Key</h3>
          <span className="llm-section__subtitle">
            Selecione uma chave do cofre ou insira diretamente
          </span>
        </div>
        <div className="llm-apikey">
          {/* Mode selector */}
          <div className="llm-apikey__modes">
            <button
              className={`llm-apikey__mode ${keyMode === 'vault' ? 'llm-apikey__mode--active' : ''}`}
              onClick={() => setKeyMode('vault')}
            >
              <IconKey size={14} />
              Cofre
            </button>
            <button
              className={`llm-apikey__mode ${keyMode === 'direct' ? 'llm-apikey__mode--active' : ''}`}
              onClick={() => setKeyMode('direct')}
            >
              <IconShieldLock size={14} />
              Chave direta
            </button>
          </div>

          {keyMode === 'vault' ? (
            <div className="llm-apikey__vault">
              {filteredVaultKeys.length > 0 ? (
                <select
                  className="llm-apikey__select"
                  value={config.vault_key_id || ''}
                  onChange={(e) => handleVaultKeySelect(e.target.value || null)}
                >
                  <option value="">Selecione uma chave...</option>
                  {filteredVaultKeys.map(k => (
                    <option key={k.id} value={k.id}>
                      {k.label} ({k.key})
                    </option>
                  ))}
                </select>
              ) : (
                <div className="llm-apikey__vault-empty">
                  <span>Nenhuma chave de {LLM_PROVIDERS[config.provider]?.name} no cofre</span>
                </div>
              )}
              <span className="llm-apikey__vault-hint">
                <IconKey size={12} />
                Gerencie suas chaves na aba "API Keys"
              </span>
              {config.vault_key_id && (
                <div className="llm-apikey__current">
                  <div className="llm-apikey__current-left">
                    <div className="llm-apikey__current-status">
                      <IconCheck size={12} />
                    </div>
                    <span className="llm-apikey__current-label">Chave do cofre selecionada</span>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <>
              <div className="llm-apikey__input-wrapper">
                <input
                  type={showApiKey ? 'text' : 'password'}
                  className="llm-apikey__input"
                  placeholder={hasCustomKey ? 'Chave configurada (insira nova para substituir)' : 'sk-...'}
                  value={newApiKey}
                  onChange={(e) => handleApiKeyChange(e.target.value)}
                />
                <button
                  type="button"
                  className="llm-apikey__toggle"
                  onClick={() => setShowApiKey(!showApiKey)}
                >
                  {showApiKey ? <IconEyeOff size={18} /> : <IconEye size={18} />}
                </button>
              </div>
              {hasCustomKey && !newApiKey && !config.vault_key_id && (
                <div className="llm-apikey__current">
                  <div className="llm-apikey__current-left">
                    <div className="llm-apikey__current-status">
                      <IconCheck size={12} />
                    </div>
                    <div className="llm-apikey__current-info">
                      <span className="llm-apikey__current-label">Chave direta configurada</span>
                      <span className="llm-apikey__current-value">{config.custom_api_key}</span>
                    </div>
                  </div>
                  <OverlayTrigger placement="top" overlay={<Tooltip>Remover chave</Tooltip>}>
                    <button
                      type="button"
                      className="llm-apikey__current-remove"
                      onClick={() => handleApiKeyChange('')}
                    >
                      <IconTrash size={14} />
                    </button>
                  </OverlayTrigger>
                </div>
              )}
              {!hasCustomKey && !newApiKey && (
                <span className="llm-apikey__hint">
                  <IconShieldLock size={12} />
                  Armazenada de forma criptografada
                </span>
              )}
            </>
          )}
        </div>
      </section>

      {/* Model Selection */}
      <section className="llm-section">
        <div className="llm-section__header">
          <h3 className="llm-section__title">Modelo</h3>
          <span className="llm-section__subtitle">
            {LLM_PROVIDERS[config.provider]?.name}
          </span>
        </div>
        <ModelSelector
          provider={config.provider}
          selectedModel={config.model}
          onSelect={(model) => handleChange('model', model)}
        />
      </section>


    </div>
  );
};

export default LLMConfigTab;
